import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import InnerPageHero from '@/components/feature/InnerPageHero';
import { useEnquiryModal } from '@/hooks/useEnquiryModal';
import { vegetableProducts, fruitProducts } from '@/mocks/products';
import { currentSeasonalProduct } from '@/mocks/seasonalProducts';
import FeaturedProductCard from '@/components/feature/FeaturedProductCard';
import FadeInSection from '@/components/base/FadeInSection';

export default function Products() {
  const [activeTab, setActiveTab] = useState<'vegetables' | 'fruits'>('vegetables');
  const { openEnquiry } = useEnquiryModal();

  const products = activeTab === 'vegetables' ? vegetableProducts : fruitProducts;

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <InnerPageHero
          bgImage="/images/products-hero.jpg"
          bgAlt="Bassi Enterprises fresh fruits and vegetables products"
          bgTitle="Bassi Enterprises - Fresh Fruits & Vegetables Products"
          breadcrumbLabel="Products"
        >
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background-50/15 backdrop-blur-sm text-background-50 rounded-full text-xs font-semibold mb-5 border border-background-50/15">
              <i className="ri-shopping-basket-2-line"></i>
              Our Produce
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 leading-tight">
              Fresh Fruits &amp; Vegetables
            </h1>
            <p className="text-background-200 text-base md:text-lg leading-relaxed">
              Premium quality fresh produce available for bulk supply. From farm-fresh vegetables to ripe seasonal fruits — all sourced with care for your business needs.
            </p>
          </div>
        </InnerPageHero>

        {/* Featured Produce Banner */}
        <section className="py-10 md:py-14 px-4 md:px-6 lg:px-10 bg-background-100">
          <FadeInSection>
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                  <i className="ri-star-fill text-accent-600 text-sm"></i>
                </div>
                <div>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                    Featured Produce Spotlight
                  </h2>
                  <p className="text-foreground-500 text-sm mt-0.5">
                    Premium seasonal produce currently in high demand
                  </p>
                </div>
              </div>
              <FeaturedProductCard
                product={currentSeasonalProduct}
                variant="banner"
                onEnquire={openEnquiry}
              />

              {/* Mango Varieties Promo */}
              <Link
                to="/mangoes"
                className="group mt-5 block relative overflow-hidden rounded-2xl border border-accent-200 bg-gradient-to-r from-accent-50 to-primary-50 hover:border-accent-300 transition-all duration-300"
              >
                <div className="flex items-center justify-between gap-4 p-5 md:p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-accent-500 flex items-center justify-center flex-shrink-0">
                      <i className="ri-sun-line text-background-50 text-xl md:text-2xl"></i>
                    </div>
                    <div>
                      <h3 className="font-heading text-base md:text-lg font-bold text-foreground-900">
                        Explore Our Fresh Mango Varieties
                      </h3>
                      <p className="text-foreground-500 text-xs md:text-sm mt-0.5">
                        Dasheri, Langra, Ramkela &amp; Chausa &mdash; graded &amp; available for bulk supply
                      </p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-accent-500 group-hover:bg-accent-600 text-background-50 text-xs md:text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap flex-shrink-0">
                    View Mangoes
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </Link>
            </div>
          </FadeInSection>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-16 md:top-20 z-40 bg-background-50 border-b border-background-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
            <div className="flex items-center gap-2 py-4">
              <button
                onClick={() => setActiveTab('vegetables')}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === 'vegetables'
                    ? 'bg-primary-500 text-background-50 shadow-sm'
                    : 'text-foreground-600 bg-background-100 hover:bg-background-200'
                }`}
              >
                <i className="ri-plant-line mr-2"></i>
                Fresh Vegetables
              </button>
              <button
                onClick={() => setActiveTab('fruits')}
                className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  activeTab === 'fruits'
                    ? 'bg-primary-500 text-background-50 shadow-sm'
                    : 'text-foreground-600 bg-background-100 hover:bg-background-200'
                }`}
              >
                <i className="ri-apple-line mr-2"></i>
                Fresh Fruits
              </button>
            </div>
          </div>
        </section>

        {/* Products Grid */}
        <section className="py-12 md:py-16 px-4 md:px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
              {products.map((product, idx) => (
                <FadeInSection key={product.name} delay={idx * 60}>
                  <div
                    className="group bg-background-50 border border-background-200 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300"
                  >
                    <div className="relative w-full aspect-square overflow-hidden bg-background-100">
                      <img
                        src={product.image}
                        alt={product.name}
                        title={`Fresh ${product.name} - Bassi Enterprises`}
                        loading="lazy"
                        className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    <div className="p-3 md:p-4">
                      <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-900 mb-1.5">
                        {product.name}
                      </h3>
                      <span className="inline-flex items-center gap-1 text-[10px] md:text-xs text-primary-600 font-medium mb-3 whitespace-nowrap">
                        <i className="ri-checkbox-circle-fill text-xs"></i>
                        Available For Bulk Supply
                      </span>
                      <button
                        onClick={() => openEnquiry(product.name)}
                        className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 text-xs md:text-sm font-semibold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-whatsapp-fill text-sm"></i>
                        Enquire Now
                      </button>
                    </div>
                  </div>
                </FadeInSection>
              ))}
            </div>

            {activeTab === 'vegetables' && (
              <p className="text-center text-foreground-500 text-sm mt-8">
                Showing {vegetableProducts.length} vegetable products available for bulk supply
              </p>
            )}
            {activeTab === 'fruits' && (
              <p className="text-center text-foreground-500 text-sm mt-8">
                Showing {fruitProducts.length} fruit products available for bulk supply
              </p>
            )}
          </div>
        </section>

        {/* Bulk Supply CTA */}
        <section className="py-14 md:py-20 px-4 md:px-6 lg:px-10 bg-primary-950 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-primary-400 blur-3xl"></div>
            <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full bg-accent-500 blur-3xl"></div>
          </div>
          <FadeInSection>
            <div className="max-w-4xl mx-auto text-center relative z-10">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4">
                Need Custom Bulk Supply?
              </h2>
              <p className="text-background-50/60 text-base md:text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
                Contact us for competitive pricing on bulk orders. We supply to restaurants, hotels, retailers, and quick-commerce companies.
              </p>
              <button
                onClick={() => openEnquiry()}
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 hover:bg-accent-400 text-background-50 font-semibold rounded-full text-base transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-fill text-xl"></i>
                Request Bulk Supply
              </button>
            </div>
          </FadeInSection>
        </section>
      </main>
      <Footer />
    </div>
  );
}
